import Icon, {
  type IconComponentProps,
} from '@ant-design/icons/lib/components/Icon';
import { type TablerIcon as TbIconType } from '@tabler/icons-react';
import { forwardRef, type SVGProps, useMemo } from 'react';

/**
 * Crea il renderer SVG da passare a `<Icon component>` per una specifica
 * icona Tabler. Definito a livello di modulo (non inline nel render) per non
 * ridichiarare un componente ad ogni render.
 *
 * Ant Design imposta `fill="currentColor"` di default, ma le icone Tabler sono
 * outline con `fill="none"` oppure filled con `fill="currentColor"` interno:
 * per supportare entrambe le varianti eliminiamo il `fill` di Ant Design.
 */
function createTbSvgRenderer(IconComp: TbIconType) {
  return function TbSvgRenderer({
    fill: _fill,
    ...svgProps
  }: SVGProps<SVGSVGElement>) {
    return <IconComp {...svgProps} shapeRendering="geometricPrecision" />;
  };
}

export interface ITbIconProps
  extends Omit<IconComponentProps, 'component' | 'ref'> {
  /** Componente icona da `@tabler/icons-react` (es. `IconHome`). */
  icon: TbIconType;
}

/**
 * Wrapper per icone della famiglia Tabler (`tb`) basato su
 * `<Icon component>` di Ant Design — lo stesso pattern usato da AgidIcon,
 * CiComputed*, ecc.
 *
 * Renderizza:
 *   <span role="img" class="anticon" aria-hidden="true">
 *     <svg width="1em" height="1em" stroke="currentColor" fill="none" aria-hidden="true">...</svg>
 *   </span>
 *
 * - `width="1em"` / `height="1em"`: gestiti da Ant Icon (ereditano font-size del parent)
 * - `fill`: `none` per le outline (default), `currentColor` interno per le
 *   filled (`<TbIcon icon={IconHomeFilled} filled />`).
 * - `aria-hidden="true"` sullo span di default (icone decorative). Se viene
 *   fornito `aria-label` o `aria-labelledby` l'icona è considerata semantica
 *   e `aria-hidden` non viene impostato (Ant gestisce `role="img"`
 *   automaticamente in quel caso).
 * - `vertical-align: -0.125em` automatico via la classe globale `.anticon`.
 *
 * Uso:
 *   import { IconHome, IconHomeFilled } from '@tabler/icons-react';
 *   import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
 *   <TbIcon icon={IconHome} />
 *   <TbIcon icon={IconHomeFilled} />
 */
export const TbIcon = forwardRef<HTMLSpanElement, ITbIconProps>(
  ({ icon: IconComp, ...props }, ref) => {
    // Icona decorativa di default → aria-hidden. Se il chiamante fornisce un
    // accessible name (aria-label / aria-labelledby) l'icona è semantica e
    // NON va nascosta agli screen reader.
    const hasAccessibleName =
      props['aria-label'] !== undefined ||
      props['aria-labelledby'] !== undefined;
    // Renderer stabile per `IconComp`: evita di rimontare l'svg ad ogni render.
    const component = useMemo(() => createTbSvgRenderer(IconComp), [IconComp]);
    return (
      <Icon
        ref={ref}
        component={component}
        aria-hidden={hasAccessibleName ? undefined : true}
        {...props}
      />
    );
  },
);
TbIcon.displayName = 'TbIcon';
